import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { DT_TYPES } from "@/lib/daytime";

const FIELDS = [
  ["date_of_birth", "Date of Birth", "date"], ["emergency_contact", "Emergency Contact Name"], ["emergency_phone", "Emergency Contact Phone", "tel"],
  ["emergency_relationship", "Relationship to Child"], ["allergies", "Allergies (write None if none)", "area"], ["medical", "Medical Conditions (or None)", "area"],
  ["special_support", "Special Needs / Support Information (or None)", "area"], ["authorized_pickups", "Authorized Pickup Persons", "area"],
];
const age = (dob) => { if (!dob) return ""; const b = new Date(dob), n = new Date(); return n.getFullYear() - b.getFullYear() - (n < new Date(n.getFullYear(), b.getMonth(), b.getDate()) ? 1 : 0); };
export const missingFields = (k) => [!k.first_name && "first name", !k.last_name && "last name", ...FIELDS.filter(([f]) => !String(k[f] || "").trim()).map(([, l]) => l.split(" (")[0])].filter(Boolean);

function ExplorerForm({ k, set, i }) {
  const f = (key, label, type) => (
    <div key={key} className={type === "area" ? "sm:col-span-2" : ""}>
      <label className="text-xs font-semibold text-slate-500">{label}</label>
      {type === "area" ? <Textarea rows={2} value={k[key] || ""} onChange={(e) => set({ ...k, [key]: e.target.value })} className="rounded-xl" data-testid={`ex-${i}-${key}`} />
        : <Input type={type || "text"} value={k[key] || ""} onChange={(e) => set({ ...k, [key]: e.target.value })} className="rounded-xl h-11" data-testid={`ex-${i}-${key}`} />}
    </div>
  );
  return (
    <div className="grid sm:grid-cols-2 gap-3 mt-3">
      {f("first_name", "Child First Name")}{f("last_name", "Child Last Name")}
      {FIELDS.map(([a, b, c]) => f(a, b, c))}
      {k.date_of_birth && <p className="text-xs text-slate-500 sm:col-span-2" data-testid={`ex-${i}-age`}>Age: <b>{age(k.date_of_birth)}</b></p>}
    </div>
  );
}

export function ExplorerStep({ slots, children, picks, setPicks, user, parent, setParent, onBack, onNext, reload }) {
  const [busy, setBusy] = useState(false);
  const setPick = (i, v) => setPicks(picks.map((p, j) => (j === i ? v : p)));
  const choose = (i, id) => setPick(i, id === "new" ? { id: "new", first_name: "", last_name: "" } : { ...children.find((c) => c.id === id) });
  const next = async () => {
    if (!parent.name.trim() || !parent.phone.trim()) return toast.error("Add the parent/guardian name and phone number");
    for (const [i, k] of picks.entries()) {
      if (!k) return toast.error(`Choose an Explorer for pass ${i + 1}`);
      const m = missingFields(k);
      if (m.length) return toast.error(`${k.first_name || "Explorer"}: please add ${m.join(", ")}`);
    }
    setBusy(true);
    try {
      if (parent.phone !== (user.phone || "") || parent.name !== (user.name || "")) await api.put("/auth/me", { name: parent.name, phone: parent.phone });
      const saved = [];
      for (const k of picks) {
        const body = { ...k, guardian_name: k.guardian_name || parent.name, parent_phone: k.parent_phone || parent.phone };
        const { data } = k.id === "new" ? await api.post("/children", body) : await api.put(`/children/${k.id}`, body);
        saved.push(data);
      }
      setPicks(saved); await reload(); onNext();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="step-explorer">
      <div className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-5">
        <p className="font-display font-black text-navy text-lg">Parent / Guardian</p>
        <div className="grid sm:grid-cols-3 gap-3 mt-2">
          <Input value={parent.name} onChange={(e) => setParent({ ...parent, name: e.target.value })} placeholder="Parent/Guardian Name" className="rounded-xl h-11" data-testid="parent-name" />
          <Input value={user.email} disabled className="rounded-xl h-11" data-testid="parent-email" />
          <Input type="tel" value={parent.phone} onChange={(e) => setParent({ ...parent, phone: e.target.value })} placeholder="Parent Phone Number" className="rounded-xl h-11" data-testid="parent-phone" />
        </div>
      </div>
      {slots.map((s, i) => {
        const k = picks[i];
        const used = picks.filter((p, j) => p && j !== i).map((p) => p.id);
        return (
          <div key={i} className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-5" data-testid={`explorer-slot-${i}`}>
            <p className="font-display font-black text-navy text-lg">{i === 0 ? "Tell Us About Your Explorer" : `Sibling ${i}`} <span className="text-sm font-semibold text-turquoise">· {DT_TYPES[i === 0 ? s : { pass2: "sib2", pass4: "sib4", allday: "sib_all" }[s]]}</span></p>
            <select value={k?.id || ""} onChange={(e) => choose(i, e.target.value)} className="mt-2 w-full h-12 rounded-xl border border-slate-200 px-3 bg-white font-semibold" data-testid={`explorer-select-${i}`}>
              <option value="">Choose…</option>
              {children.filter((c) => !used.includes(c.id)).length > 0 && <optgroup label="My Explorer already has a profile">
                {children.filter((c) => !used.includes(c.id)).map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}</optgroup>}
              <option value="new">+ Create a New Explorer Profile</option>
            </select>
            {k && <ExplorerForm k={k} set={(v) => setPick(i, v)} i={i} />}
          </div>
        );
      })}
      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} className="h-14 rounded-full px-6" data-testid="step-explorer-back">Back</Button>
        <Button disabled={busy} onClick={next} className="flex-1 h-14 rounded-full bg-navy text-white font-black" data-testid="step-explorer-next">{busy ? "Saving…" : "NEXT: PLAY OPTIONS & EXTRAS"}</Button>
      </div>
    </div>
  );
}
